import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Form, Input, notification, Rate, Upload } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { MdOutlineMailOutline } from "react-icons/md";
import { LuMessageSquare } from "react-icons/lu";
import { RiGalleryLine } from "react-icons/ri";
import { PiDownloadSimple } from "react-icons/pi";
import { RiSendPlaneFill } from "react-icons/ri";
import feedbackService from '../services/feedback-service';
import { changeDocumentTitle } from '../utils/title';

function Feedback() {
    const [form] = Form.useForm()
    const [selected, setSelected] = useState([]);
    const [value, setValue] = useState(0) // initial rating value
    const [loading, setLoading] = useState(false)
    const [visible, setVisible] = useState(false)
    const [fileList, setFileList] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false)

    const toggleSelect = (item) => {
        setSelected((prev) => {
            const updated = prev.includes(item)
                ? prev.filter((val) => val !== item)
                : [...prev, item];
            form.setFieldValue("area_of_feedbacks", updated);
            return updated;
        });
    };

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const options = [
        'Booking and Rates',
        'Vehicles and Yard',
        'Invoices',
        'Ledger',
        'Loading Plan',
        'Appointments',
        'Design and Usability',
        'Speed and Performance',
        'Other',
    ];
    const ratingTexts = {
        1: "One star, very poor",
        2: "Two stars, needs improvement",
        3: "Three stars, okay",
        4: "Four stars, very good",
        5: "Five stars, excellent"
    }

    const onFinish = async (values) => {
        setLoading(true);
        setVisible(true)
        const user = JSON.parse(localStorage.getItem("user"));
        values.customer_id = user.id;

        const formData = new FormData();
        formData.append("customer_id", values.customer_id);
        formData.append("rating", values.rating);
        formData.append("feedback", values.feedback);
        values.area_of_feedbacks.forEach((item) => {
            formData.append("area_of_feedbacks[]", item);
        });
        if (values.attachments && values.attachments.fileList) {
            values.attachments.fileList.forEach((file) => {
                formData.append("attachments[]", file.originFileObj);
            });
        }
        const response = await feedbackService.add(formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        notification.success({
            description: (
                <span style={{ color: '#006045', whiteSpace: 'nowrap' }}>
                    Feedback submitted successfully (ID: FB-{response?.data?.id || 'FB-0000'})
                </span>
            ),
            placement: 'bottom', // 👈 show at bottom center
            duration: 3, // seconds
            style: {
                width: 450,
                backgroundColor: "#ECFDF5",
            },
        });
        setLoading(false);
        form.resetFields();
        setValue(0)
        setFileList([]);
        setSelected([]);
    };

    useEffect(() => {
        changeDocumentTitle("Feedback");
    }, [])
    return (
        <div className='w-full'>
            <Form onFinish={onFinish} form={form}>
                <div className='w-full p-8  bg-[#EAEAEA] rounded-xl'>
                    <p className='text-3xl font-semibold p-0 mb-2'>Tell us how we can improve</p>
                    <p className='text-[#737373] font-normal p-0 m-0'>Share feedback about services and the web application. Your input helps the team prioritize fixes and new features.</p>
                </div>
                <div className='flex mt-8 gap-5 '>
                    <div className='border bg-white rounded-2xl p-5 w-[70%] h-auto gap-6'>
                        <div className='w-full h-auto pr-5'>
                            <p className='text-lg font-semibold'>Overall service rating</p>
                            <div className='flex items-center gap-4 '>
                                <Form.Item name="rating" className='m-0 p-0'>
                                    <Rate value={value} onChange={setValue} style={{ fontSize: 30 }} />
                                </Form.Item>
                                <p className='text-sm font-medium mb-2 p-0'>
                                    {value === 0 ? "Select rating" : `${value} of 5`}
                                </p>
                            </div>
                            <p className={`pt-2 text-sm text-[#737373] ${value === 1 || value === 2 || value === 3 ? 'text-red-600' : ""}`}>
                                {ratingTexts[value] || ""}
                            </p>
                        </div>
                        <div className='w-full h-auto'>
                            <p className='text-lg font-semibold'>Area of feedback</p>
                            <Form.Item name="area_of_feedbacks">
                                <div className='flex gap-3 flex-wrap'>
                                    {options.map((item) => {
                                        const isSelected = selected.includes(item);
                                        return (
                                            <div
                                                key={item}
                                                onClick={() => toggleSelect(item)}
                                                className={`flex items-center justify-between gap-2 px-2 py-3 rounded-2xl border text-sm font-medium transition-all cursor-pointer 
                    ${isSelected ? ' text-primary border-primary'
                                                        : 'bg-[#F9F9F9] text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <span
                                                    className={`w-3 h-3 flex items-center justify-center rounded-full border transition-all
                      ${isSelected
                                                            ? 'bg-white text-primary border-primary'
                                                            : 'bg-transparent border-gray-400 text-transparent'
                                                        }`}
                                                >
                                                    <CheckOutlined style={{ fontSize: '8px' }} />
                                                </span>
                                                <span className='text-xs'>{item}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Form.Item>
                            <p className='pt-1 text-xs text-[#737373]'> Pick all that apply.</p>
                        </div>
                    </div>

                    <div className='border p-5 h-auto rounded-2xl bg-white w-[30%]'>
                        <p className='text-lg font-semibold'>Tips for clear reports</p>
                        <ul className='list-disc space-y-4 p-5'>
                            <li>Mention the page where the issue happened.</li>
                            <li>Write the steps so the team can reproduce the problem.</li>
                            <li>Add a screenshot if possible.</li>
                            <li>Include your booking id or container id when relevant.</li>
                        </ul>
                    </div>
                </div>

                <div className='flex gap-3 mt-6'>
                    <div className='border rounded-2xl  p-5 bg-white w-[70%]'>
                        <div className=''>
                            <p className='text-base font-semibold'>Your feedback</p>
                            <Form.Item name="feedback">
                                <Input.TextArea
                                    rows={6}
                                    placeholder="Share your thoughts..."
                                />
                            </Form.Item>
                            <p className='text-xs text-[#737373] mt-1' >At least 10 characters</p>
                        </div>
                        <div className='border p-4 rounded-xl flex justify-between'>

                            <div className='flex items-center gap-2'>
                                <RiGalleryLine style={{ fontSize: 40, border: '1px solid #E0E0E0', padding: 8, backgroundColor: "#FAFAFA", borderRadius: 12, color: "#737373" }} />
                                <div className=' '>
                                    <p className='p-0 m-0 text-lg font-medium'>Attach screenshots or files</p>
                                    <p className='p-0 m-0'>Drag and drop, or click to upload. Up to 5 files, 10 MB each.</p>
                                </div>
                            </div>
                            <Form.Item name='attachments'>
                                <Upload
                                    fileList={fileList}
                                    onChange={handleChange}
                                    multiple
                                    className='upload_image'
                                    beforeUpload={() => false}>
                                    <Button icon={<PiDownloadSimple style={{ fontSize: 20 }} />} className='rounded-xl h-10'>
                                        Browse
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>

                        <div className='flex items-center mt-5 gap-4'>
                            <Checkbox onChange={(e) => setVisible(e.target.checked)} />
                            You agree that the team may contact you about this feedback and ctrade usa the submitted data for product improvement.
                        </div>

                        <div className='flex items-center  justify-between mt-10'>
                            <p className=' text-xs text-[#737373]'>By submitting, you accept the acceptable use policy and privacy terms.</p>
                            <Button htmlType='submit' loading={loading}
                                disabled={visible ? false : true}
                                type='primary' icon={<RiSendPlaneFill style={{ fontSize: 20 }} />} className='rounded-xl h-10'>
                                Submit Feedback
                            </Button>
                        </div>
                    </div>

                    <div className='border p-5 h-60 rounded-2xl w-[30%] bg-white'>
                        <p className='text-lg font-semibold'>Need quick help</p>
                        <p className='text-base'>Use chat inside the portal for urgent items, or email support for follow up.</p>
                        <p className='flex items-center gap-3'> <LuMessageSquare style={{ color: "#404040", fontSize: 20 }} />
                            Live chat in portal</p>
                        <p className='flex items-center gap-3'> <MdOutlineMailOutline style={{ color: "#404040", fontSize: 20 }} /> support@ctrade.us</p>
                    </div>
                </div>
            </Form >
            <style>
                {
                    `
.upload_image .ant-upload-list-item-name {
  max-width: 180px;       /* limit width */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}

`

                }
            </style>
        </div >
    )
}

export default Feedback